import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import type { DragEndEvent } from "@dnd-kit/core"
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import clsx from "clsx"

import { TablePagination } from "components/paginations/TablePagination"

import { gameService } from "services/game/game.service"
import { GameInterface, SectionOrderGame } from "services/game/type"

import { GeoEditingBanner } from "./GeoEditingBanner"
import { SearchGamesPanel } from "./SearchGamesPanel"
import { SortableRow } from "./SortableRow"

const PAGE_SIZE = 50

interface SectionOrderEditorProps {
  sectionKey: string
  geo: string
  onBack: () => void
  // When true (used for MANUAL Categories), the editor exposes an "Add games"
  // search above the table and a remove (×) button on each row. For BUILTIN
  // sections (slots/live/etc.) the membership is filter-derived and these
  // controls are hidden — admins can only reorder, not add/remove.
  allowAddRemove?: boolean
}

export const SectionOrderEditor = ({ sectionKey, geo, onBack, allowAddRemove }: SectionOrderEditorProps) => {
  const [gameIds, setGameIds] = useState<string[]>([])
  console.log("gameIds: ", gameIds)
  const [originalGameIds, setOriginalGameIds] = useState<string[]>([])
  const [pageGames, setPageGames] = useState<SectionOrderGame[]>([])
  const [page, setPage] = useState(1)
  const [isFork, setIsFork] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // For MANUAL Categories: cache of details for games the admin added in this
  // editing session. Backend's paginated `pageGames` doesn't include these
  // until after save, so we render from the cache when the visible page
  // includes any locally-added IDs.
  const [addedDetails, setAddedDetails] = useState<Record<string, SectionOrderGame>>({})

  const dirty = useMemo(() => {
    if (gameIds.length !== originalGameIds.length) return true
    for (let i = 0; i < gameIds.length; i++) {
      if (gameIds[i] !== originalGameIds[i]) return true
    }
    return false
  }, [gameIds, originalGameIds])

  const totalPages = Math.max(1, Math.ceil(gameIds.length / PAGE_SIZE))

  // Computed list of games rendered in the table. The admin may have added
  // games (MANUAL only) or moved games into this page from another page via
  // the search-and-move flow; their details aren't on the server-paginated
  // response yet, so we look them up in `addedDetails`. Order matches the
  // local gameIds (the source of truth after local edits).
  const displayedPageGames = useMemo<SectionOrderGame[]>(() => {
    const sliceIds = gameIds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    const detailById = new Map(pageGames.map((g) => [g.identifier, g]))
    return sliceIds.map((id) => detailById.get(id) ?? addedDetails[id]).filter((g): g is SectionOrderGame => Boolean(g))
  }, [gameIds, page, pageGames, addedDetails])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const load = useCallback(
    async (targetPage: number, syncOriginal: boolean) => {
      setLoading(true)
      const data = await gameService.getSectionOrder(sectionKey, { page: targetPage, pageSize: PAGE_SIZE, geo })
      setLoading(false)
      if (!data) return
      setPage(data.page)
      setPageGames(data.pageGames)
      setGameIds(data.gameIds)
      setIsFork(data.isFork)
      if (syncOriginal) setOriginalGameIds(data.gameIds)
    },
    [sectionKey, geo]
  )

  useEffect(() => {
    load(1, true)
  }, [load])

  const handleAddGame = (g: GameInterface) => {
    // Append at end of the global list; admin can drag it elsewhere afterwards.
    setGameIds((prev) => (prev.includes(g.identifier) ? prev : [...prev, g.identifier]))
    setAddedDetails((prev) => ({
      ...prev,
      [g.identifier]: {
        identifier: g.identifier,
        title: g.title,
        image: g.image ?? null,
        image_vertical: g.image_vertical ?? null,
        image_override: g.image_override ?? null,
        image_vertical_override: g.image_vertical_override ?? null,
        enabled: g.enabled,
        provider: g.provider,
        gameProvider: g.gameProvider ? { identifier: g.gameProvider.identifier, title: g.gameProvider.title } : null
      }
    }))
    toast.success(`Added: ${g.title}`)
  }

  const handleRemoveGame = (gameId: string) => {
    setGameIds((prev) => prev.filter((id) => id !== gameId))
    setPageGames((prev) => prev.filter((g) => g.identifier !== gameId))
  }

  // Move an already-included game to a specific 1-based position. Updates
  // gameIds locally, stashes the moved game's details (so it renders on the
  // destination page even before save), and navigates to the destination page
  // so the admin can see the result of their move.
  const handleMoveTo = async (g: GameInterface, newPosition: number) => {
    const currentIdx = gameIds.indexOf(g.identifier)
    if (currentIdx === -1) return
    if (!Number.isInteger(newPosition) || newPosition < 1 || newPosition > gameIds.length) {
      toast.error(`Position must be between 1 and ${gameIds.length}`)
      return
    }
    const newIdx = newPosition - 1
    if (currentIdx === newIdx) {
      toast.info(`"${g.title}" is already at position ${newPosition}`)
      return
    }

    const newGameIds = arrayMove(gameIds, currentIdx, newIdx)
    const movedDetail: SectionOrderGame = {
      identifier: g.identifier,
      title: g.title,
      image: g.image ?? null,
      image_vertical: g.image_vertical ?? null,
      image_override: g.image_override ?? null,
      image_vertical_override: g.image_vertical_override ?? null,
      enabled: g.enabled,
      provider: g.provider,
      gameProvider: g.gameProvider ? { identifier: g.gameProvider.identifier, title: g.gameProvider.title } : null
    }

    setGameIds(newGameIds)
    setAddedDetails((prev) => ({ ...prev, [g.identifier]: movedDetail }))

    // Navigate to the destination page so the admin sees the move land. Fetch
    // server pageGames for that page and reconcile against the local order;
    // fall back to addedDetails / the just-moved detail for any ID the server
    // doesn't return (game was moved here from another page).
    const destPage = Math.floor(newIdx / PAGE_SIZE) + 1
    setLoading(true)
    const data = await gameService.getSectionOrder(sectionKey, { page: destPage, pageSize: PAGE_SIZE, geo })
    setLoading(false)
    if (data) {
      setPage(destPage)
      const localSlice = newGameIds.slice((destPage - 1) * PAGE_SIZE, destPage * PAGE_SIZE)
      const detailById = new Map(data.pageGames.map((dg) => [dg.identifier, dg]))
      const ordered = localSlice
        .map((id) => detailById.get(id) ?? addedDetails[id] ?? (id === g.identifier ? movedDetail : undefined))
        .filter((og): og is SectionOrderGame => Boolean(og))
      setPageGames(ordered)
    }

    toast.success(`Moved "${g.title}" to position ${newPosition}`)
  }

  const handlePageChange = (next: number) => {
    if (dirty) {
      const fetchAndReconcile = async () => {
        setLoading(true)
        const data = await gameService.getSectionOrder(sectionKey, { page: next, pageSize: PAGE_SIZE, geo })
        setLoading(false)
        if (!data) return
        setPage(next)
        // Reorder server pageGames according to the current local slice. Fall
        // back to addedDetails for any IDs the server didn't return (game was
        // moved here from another page during this session).
        const localSlice = gameIds.slice((next - 1) * PAGE_SIZE, next * PAGE_SIZE)
        const detailById = new Map(data.pageGames.map((g) => [g.identifier, g]))
        const ordered = localSlice.map((id) => detailById.get(id) ?? addedDetails[id]).filter((g): g is SectionOrderGame => Boolean(g))
        setPageGames(ordered)
      }
      fetchAndReconcile()
    } else {
      load(next, false)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldVisibleIdx = pageGames.findIndex((g) => g.identifier === active.id)
    const newVisibleIdx = pageGames.findIndex((g) => g.identifier === over.id)
    if (oldVisibleIdx === -1 || newVisibleIdx === -1) return

    // Reorder the visible page in display order.
    setPageGames(arrayMove(pageGames, oldVisibleIdx, newVisibleIdx))

    // Reflect the same move in the full gameIds array.
    const globalOldIdx = (page - 1) * PAGE_SIZE + oldVisibleIdx
    const globalNewIdx = (page - 1) * PAGE_SIZE + newVisibleIdx
    setGameIds(arrayMove(gameIds, globalOldIdx, globalNewIdx))
  }

  const handleSave = async () => {
    if (!dirty || saving) return
    setSaving(true)
    const ok = await gameService.replaceSectionOrder(sectionKey, gameIds, geo)
    setSaving(false)
    if (ok) {
      toast.success(geo === "*" ? "Order saved (global default)" : `Order saved for ${geo}`)
      setOriginalGameIds(gameIds)
      setIsFork(true)
    }
  }

  const handleRevert = () => {
    setGameIds(originalGameIds)
    // Within-page drag never moves games across pages — the current page's IDs
    // are unchanged, only their order differs. So pageGames already has all
    // the details we need; just reorder them per originalGameIds' page slice.
    const originalSlice = originalGameIds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    const detailById = new Map(pageGames.map((g) => [g.identifier, g]))
    const ordered = originalSlice.map((id) => detailById.get(id)).filter((g): g is SectionOrderGame => Boolean(g))
    setPageGames(ordered)
  }

  // Per-section CSV export/import — scoped to THIS sectionKey only. The
  // bulk variants live in SectionsTab (across all sections). Import rejects
  // any row whose category_key doesn't match this section so admins can't
  // accidentally clobber a different one.
  const [exportingCsv, setExportingCsv] = useState(false)
  const [importingCsv, setImportingCsv] = useState(false)

  const handleExportCsv = async () => {
    if (exportingCsv) return
    setExportingCsv(true)
    await gameService.exportSectionOrderCsv(sectionKey)
    setExportingCsv(false)
  }

  const handleImportCsv = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    setImportingCsv(true)
    const result = await gameService.importSectionOrderCsv(sectionKey, file)
    setImportingCsv(false)
    if (result) await load(1, true) // reload from page 1, sync original = no dirty state
  }

  const tableHeaders = [
    { label: "#", className: "px-3 py-2 w-12" },
    { label: "", className: "px-3 py-2 w-12" },
    { label: "Image", className: "px-3 py-2" },
    { label: "Title", className: "px-3 py-2" },
    { label: "Provider", className: "px-3 py-2" },
    { label: "Enabled", className: "px-3 py-2 text-center w-24" },
    ...(allowAddRemove ? [{ label: "", className: "px-3 py-2 w-12" }] : [])
  ]

  return (
    <div className="flex flex-col gap-4">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="btn btn-ghost btn-sm gap-1 border border-[rgba(132,_0,_168,_0.25)] text-[rgba(6,7,21,1)] dark:text-white -ml-1"
        >
          ← Back
        </button>
      </div>

      <GeoEditingBanner geo={geo} isFork={isFork} />

      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-semibold text-xl text-[rgba(6,7,21,1)] dark:text-white capitalize">{sectionKey} — game order</h2>
          <p className="text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
            Drag-drop to reorder games within the current page. Save commits the full section order — cross-page positions are preserved.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exportingCsv || loading}
            className="btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] disabled:opacity-40"
            title={`Download GameCategoryOrder rows for "${sectionKey}" (all geos) as CSV`}
          >
            {exportingCsv ? "Exporting…" : "Export CSV"}
          </button>
          <label
            className={clsx(
              "btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06]",
              importingCsv && "btn-disabled opacity-40"
            )}
            title={`Replace game ordering for "${sectionKey}" (per geo) from a CSV file. Rows for other sections are rejected.`}
          >
            {importingCsv ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportCsv} disabled={importingCsv} />
          </label>
          <button
            type="button"
            onClick={handleRevert}
            disabled={!dirty || saving || loading}
            className="btn btn-ghost btn-sm border border-[rgba(132,_0,_168,_0.25)] text-[rgba(6,7,21,1)] dark:text-white disabled:opacity-40"
          >
            Revert
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || loading}
            className={clsx(
              "btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent disabled:opacity-40"
            )}
          >
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </header>

      <SearchGamesPanel
        allowAddRemove={!!allowAddRemove}
        gameIds={gameIds}
        onMoveGame={handleMoveTo}
        onAddGame={handleAddGame}
      />

      {loading && gameIds.length === 0 ? (
        <p className="py-8 text-center text-lg text-[rgba(6,7,21,1)] dark:text-white">Loading…</p>
      ) : (
        <>
          <div className="rounded-lg border border-black/10 dark:border-white/20 overflow-hidden">
            <table className="table table-compact w-full bg-white dark:bg-transparent text-[rgba(6,7,21,1)] dark:text-white">
              <thead className="bg-black/5 dark:bg-white/10 text-sm">
                <tr>
                  {tableHeaders.map((header, index) => (
                    <th key={index} className={header.className}>
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={displayedPageGames.map((g) => g.identifier)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {displayedPageGames.map((g, i) => (
                      <SortableRow
                        key={g.identifier}
                        game={g}
                        positionLabel={(page - 1) * PAGE_SIZE + i + 1}
                        onRemove={allowAddRemove ? () => handleRemoveGame(g.identifier) : undefined}
                      />
                    ))}
                    {displayedPageGames.length === 0 && !loading && (
                      <tr>
                        <td colSpan={allowAddRemove ? 7 : 6} className="px-3 py-8 text-center text-sm opacity-70">
                          {allowAddRemove ? "No games yet. Use search above to add some." : "No games in this section."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>

          {totalPages > 1 && <TablePagination page={page} totalPages={totalPages} onJump={handlePageChange} />}
        </>
      )}
    </div>
  )
}
